// ── JobSpec Telemetry (Layer 3c) ──
// Read-only selector that aggregates real, durable signal for a published
// JobSpec version. Joins event/execution state to job.boundJobSpec at read
// time. Does NOT mutate, persist, or invent proxies. Time-in-phase is NOT
// derivable from current data — surface honest empty state instead.

import type { Job } from "./types";
import type { JobSpecExecution } from "./jobSpecExecution";
import type { ProofGapEvent } from "./installerProof";
import type { JobSpec, JobSpecPhase, JobSpecPhaseName } from "./jobSpec";
import type { ProofPolicy } from "./installerProof";

export interface PhotoGapStat {
  photoType: string;
  phase: JobSpecPhaseName;
  missCount: number;
  observedSubmits: number;
  rate: number; // 0..1
}

export interface ChecklistSkipStat {
  itemId: string;
  itemLabel: string;
  phase: JobSpecPhaseName;
  skipCount: number;
  observedSubmits: number;
  rate: number; // 0..1
}

export interface PolicyDistribution {
  policy: ProofPolicy;
  count: number;
}

export interface SpecVersionTelemetry {
  specId: string;
  versionNumber: number;
  /** Jobs currently bound to this exact spec version. */
  boundJobCount: number;
  /** Total phase-submit observations (any phase, any job, this spec version). */
  totalPhaseSubmits: number;
  /** Most-missed required photos (descending), limited to top 10. */
  photoGaps: PhotoGapStat[];
  /** Most-skipped required checklist items (descending), limited to top 10. */
  checklistSkips: ChecklistSkipStat[];
  /** Submit count grouped by proof policy at submit time. */
  policyDistribution: PolicyDistribution[];
  /**
   * Honest empty-state flags so the UI can refuse to show zero-as-data.
   */
  empty: {
    /** No bound jobs at all — no execution history yet. */
    noBoundJobs: boolean;
    /** No proof-gap events recorded for this spec version. */
    noProofGapEvents: boolean;
    /** Always true today — phase enter/exit timestamps are not captured. */
    phaseTimingNotCaptured: true;
  };
}

/**
 * Compute telemetry for one published spec version. All inputs are
 * canonical, read-only sources of truth in the app.
 *
 * Attribution rule: jobs → boundJobSpec carries the {specId, versionNumber}.
 * Bound jobs are immutable per the JobSpec System memory, so this join is
 * lossless and deterministic. Events that reference a job inherit the
 * spec attribution of that job.
 */
export function getSpecVersionTelemetry(
  args: {
    spec: JobSpec;
    jobs: Job[];
    specExecutions: JobSpecExecution[];
    proofGapEvents: ProofGapEvent[];
  },
): SpecVersionTelemetry {
  const { spec, jobs, specExecutions, proofGapEvents } = args;

  // Jobs bound to THIS spec version specifically.
  const boundJobs = jobs.filter(
    (j) =>
      j.boundJobSpec?.specId === spec.specId &&
      j.boundJobSpec?.versionNumber === spec.versionNumber,
  );
  const boundJobIds = new Set(boundJobs.map((j) => j.id));

  // Executions for those jobs (also matched on specRef as belt + suspenders).
  const versionExecs = specExecutions.filter(
    (e) =>
      boundJobIds.has(e.jobId) &&
      e.specRef.specId === spec.specId &&
      e.specRef.versionNumber === spec.versionNumber,
  );

  // ── Submit volume ─────────────────────────────────────────────────
  // Count phase records that have moved past not_started/in_progress —
  // i.e. installer pressed submit at least once.
  const SUBMITTED: ReadonlySet<string> = new Set([
    "submitted",
    "qa_passed",
    "qa_failed",
  ]);
  let totalPhaseSubmits = 0;
  // Per-phase submit counts (denominator for gap rate).
  const submitsByPhase = new Map<JobSpecPhaseName, number>();
  for (const exec of versionExecs) {
    for (const phase of exec.phases) {
      if (SUBMITTED.has(phase.status)) {
        totalPhaseSubmits += 1;
        submitsByPhase.set(
          phase.phaseName,
          (submitsByPhase.get(phase.phaseName) ?? 0) + 1,
        );
      }
    }
  }

  // Proof-gap events scoped to this spec version.
  // Layer 3d: prefer embedded attribution (specId/specVersion captured at
  // emission time). Only fall back to the boundJobIds join for LEGACY events
  // that predate Layer 3d and have no embedded attribution. This prevents
  // historical events from re-attributing if a job is later rebound.
  const versionGapEvents = proofGapEvents.filter((g) => {
    const hasEmbedded = g.specId !== undefined && g.specVersion !== undefined;
    if (hasEmbedded) {
      return g.specId === spec.specId && g.specVersion === spec.versionNumber;
    }
    // Legacy event — fall back to current job binding.
    return boundJobIds.has(g.jobId);
  });

  // Index spec phases by name for label lookup.
  const phaseByName = new Map<JobSpecPhaseName, JobSpecPhase>();
  for (const p of spec.phases) phaseByName.set(p.name, p);

  // ── Photo gap rates ───────────────────────────────────────────────
  const photoMisses = new Map<string, { phase: JobSpecPhaseName; count: number }>();
  for (const ev of versionGapEvents) {
    // Map phaseId → phase name. phaseId in events is "<jobId>-<phaseName>"
    // pattern OR the phase name directly. Spec phase names are the canonical
    // axis we attribute against; fall back to "unknown" only for sanity.
    const phaseName = (spec.phases.find((p) => ev.phaseId.includes(p.name))?.name ??
      ("unknown" as JobSpecPhaseName));
    for (const photo of ev.missingPhotos) {
      const key = `${phaseName}::${photo}`;
      const prev = photoMisses.get(key) ?? { phase: phaseName, count: 0 };
      prev.count += 1;
      photoMisses.set(key, prev);
    }
  }
  const photoGaps: PhotoGapStat[] = [];
  for (const [key, { phase, count }] of photoMisses) {
    const photoType = key.split("::")[1];
    const denom = submitsByPhase.get(phase) ?? 0;
    photoGaps.push({
      photoType,
      phase,
      missCount: count,
      observedSubmits: denom,
      rate: denom > 0 ? count / denom : 0,
    });
  }
  photoGaps.sort((a, b) => b.missCount - a.missCount);

  // ── Checklist skip rates ──────────────────────────────────────────
  const checkMisses = new Map<string, { phase: JobSpecPhaseName; count: number }>();
  for (const ev of versionGapEvents) {
    const phaseName = (spec.phases.find((p) => ev.phaseId.includes(p.name))?.name ??
      ("unknown" as JobSpecPhaseName));
    for (const itemId of ev.missingChecklist) {
      const key = `${phaseName}::${itemId}`;
      const prev = checkMisses.get(key) ?? { phase: phaseName, count: 0 };
      prev.count += 1;
      checkMisses.set(key, prev);
    }
  }
  const checklistSkips: ChecklistSkipStat[] = [];
  for (const [key, { phase, count }] of checkMisses) {
    const itemId = key.split("::")[1];
    const itemLabel =
      phaseByName.get(phase)?.checklistItems.find((c) => c.id === itemId)?.label ?? itemId;
    const denom = submitsByPhase.get(phase) ?? 0;
    checklistSkips.push({
      itemId,
      itemLabel,
      phase,
      skipCount: count,
      observedSubmits: denom,
      rate: denom > 0 ? count / denom : 0,
    });
  }
  checklistSkips.sort((a, b) => b.skipCount - a.skipCount);

  // ── Policy distribution ───────────────────────────────────────────
  const policyMap = new Map<ProofPolicy, number>();
  for (const ev of versionGapEvents) {
    policyMap.set(ev.policyAtSubmit, (policyMap.get(ev.policyAtSubmit) ?? 0) + 1);
  }
  const policyDistribution: PolicyDistribution[] = [];
  for (const [policy, count] of policyMap) {
    policyDistribution.push({ policy, count });
  }
  policyDistribution.sort((a, b) => b.count - a.count);

  return {
    specId: spec.specId,
    versionNumber: spec.versionNumber,
    boundJobCount: boundJobs.length,
    totalPhaseSubmits,
    photoGaps: photoGaps.slice(0, 10),
    checklistSkips: checklistSkips.slice(0, 10),
    policyDistribution,
    empty: {
      noBoundJobs: boundJobs.length === 0,
      noProofGapEvents: versionGapEvents.length === 0,
      phaseTimingNotCaptured: true,
    },
  };
}

// ── Advisory recommendations (Layer 3c, scope-locked) ────────────────
// Pure function over telemetry. Only proof-gap-derived. No auto-mutation.

export type SpecRecommendationSeverity = "info" | "advisory" | "attention";

export interface SpecRecommendation {
  id: string;
  severity: SpecRecommendationSeverity;
  message: string;
}

const HIGH_GAP_RATE = 0.4; // ≥40% of submits miss this required item
const REPEATED_MISS = 3; // raw count floor so we don't advise on n=1

export function getSpecRecommendations(t: SpecVersionTelemetry): SpecRecommendation[] {
  const recs: SpecRecommendation[] = [];

  if (t.empty.noBoundJobs || t.empty.noProofGapEvents) {
    return recs; // honest: no signal yet
  }

  for (const g of t.photoGaps) {
    if (g.observedSubmits >= REPEATED_MISS && g.rate >= HIGH_GAP_RATE) {
      recs.push({
        id: `photo-${g.phase}-${g.photoType}`,
        severity: "attention",
        message: `Required photo "${g.photoType}" is missing on ${Math.round(
          g.rate * 100,
        )}% of ${g.phase} submits. Consider rewording the requirement or splitting the capture step.`,
      });
    }
  }

  for (const c of t.checklistSkips) {
    if (c.observedSubmits >= REPEATED_MISS && c.rate >= HIGH_GAP_RATE) {
      recs.push({
        id: `check-${c.phase}-${c.itemId}`,
        severity: "advisory",
        message: `Checklist item "${c.itemLabel}" is being skipped on ${Math.round(
          c.rate * 100,
        )}% of ${c.phase} submits. Consider clarifying the item or removing it if it is no longer load-bearing.`,
      });
    }
  }

  // Permissive policy dominance signal — "advisory" is the most-permissive mode.
  const total = t.policyDistribution.reduce((s, p) => s + p.count, 0);
  const permissive =
    t.policyDistribution.find((p) => p.policy === "advisory")?.count ?? 0;
  if (total >= REPEATED_MISS && permissive / total >= 0.6) {
    recs.push({
      id: "policy-permissive-dominant",
      severity: "info",
      message: `${Math.round(
        (permissive / total) * 100,
      )}% of submits used a permissive proof policy. Gap rates above may understate true compliance under stricter modes.`,
    });
  }

  return recs.slice(0, 6);
}
